/**
 * Per-user FloatingPanel prefs in localStorage (plan 15.4 "Memory").
 * Key: d5.brain.v1:<username>. Every access is wrapped: storage can be absent,
 * blocked (private mode) or full, and a broken pref must never break the panel.
 */
import { BRAIN_STORAGE_PREFIX, PANEL_STATES } from "./floatingPanelConstants";

// Maximised is never remembered (owner, F1): a reload always reopens small.
const PERSISTABLE_STATES = new Set([PANEL_STATES.BUBBLE, PANEL_STATES.SMALL]);

/** The state to store for a live state: maximised is saved as small, hidden as nothing. */
export const persistableState = (state) => {
  if (state === PANEL_STATES.MAXIMISED) return PANEL_STATES.SMALL;
  return PERSISTABLE_STATES.has(state) ? state : null;
};

export const brainStorageKey = (username) =>
  username ? `${BRAIN_STORAGE_PREFIX}${username}` : null;

const isFiniteNumber = (v) => typeof v === "number" && Number.isFinite(v);

/** Drop anything malformed; returns only fields that are safe to use. */
export function sanitizePrefs(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const out = {};
  const state = persistableState(raw.state);
  if (state) out.state = state;
  if (isFiniteNumber(raw.x) && isFiniteNumber(raw.y)) { out.x = raw.x; out.y = raw.y; }
  if (isFiniteNumber(raw.width) && raw.width > 0 && isFiniteNumber(raw.height) && raw.height > 0) {
    out.width = raw.width;
    out.height = raw.height;
  }
  if (typeof raw.tab === "string" && raw.tab) out.tab = raw.tab;
  return out;
}

const getStorage = () => {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch (_) {
    return null;
  }
};

export function loadPanelPrefs(key) {
  if (!key) return {};
  const storage = getStorage();
  if (!storage) return {};
  try {
    const raw = storage.getItem(key);
    return raw ? sanitizePrefs(JSON.parse(raw)) : {};
  } catch (_) {
    return {};
  }
}

/**
 * Merge `patch` into the stored prefs. Fields not in the patch are kept, so
 * the panel shell (geometry, state) and the panel content (tab) can each
 * save their own part. Returns true when the write succeeded.
 */
export function savePanelPrefs(key, patch) {
  if (!key) return false;
  const storage = getStorage();
  if (!storage) return false;
  try {
    const merged = sanitizePrefs({ ...loadPanelPrefs(key), ...patch });
    storage.setItem(key, JSON.stringify(merged));
    return true;
  } catch (_) {
    return false;
  }
}
